/**
 * SMS Service
 *
 * SMS 인증 관련 API 호출을 처리합니다.
 *
 * 계약 (product-spec v5 §3.3 재설계):
 *  - user_id 전송 금지 [C-16]. 로그인 재인증은 서명 챌린지(smsChallenge)로만 userId 확정.
 *  - 가입용 SMS 는 purpose='register' 로 발송하고, verify 응답의 data.verificationId 를
 *    RegisterPayload.smsVerificationId 로 전달한다 [C-32].
 */

// 상대 경로 사용 (Nginx 리버스 프록시를 통해 /api로 라우팅됨)
const API_BASE_URL = '';

/** SMS 인증 용도 — 서버 계약과 일치 */
export type SmsPurpose = 'register' | 'login' | 'home_styling' | 'repair_request';

/** send/verify/resend 공통 옵션. user_id 는 절대 포함하지 않는다 [C-16]. */
export interface SmsRequestOptions {
  purpose?: SmsPurpose;   // 미지정 시 서버 기본(home_styling)
  smsChallenge?: string;  // 로그인 재인증 서명 챌린지
}

interface SendSMSResponse {
  success: boolean;
  message: string;
  data?: {
    expires_in: number;
  };
  error?: {
    code: string;
    message: string;
    retryAfter?: number;
  };
}

interface VerifySMSResponse {
  success: boolean;
  message: string;
  data?: {
    phone_number: string;
    verified: boolean;
    // register purpose verify 성공 시 반환되는 1회성 proof id [C-32]
    verificationId?: string;
  };
  error?: {
    code: string;
    message: string;
  };
  // JWT 토큰 관련 필드 (로그인 재인증 성공 시 포함됨)
  token?: string;
  refreshToken?: string;
  user?: {
    id: string;
    email: string;
    name: string;
    role: string;
    phone_verified: boolean;
  };
  expiresIn?: number;
}

interface SMSStatusResponse {
  success: boolean;
  data?: {
    enabled: boolean;
    mode: 'development' | 'production';
    region: string;
    senderId: string;
    codeExpiry: number;
    mockMode: boolean;
  };
}

/** 옵션에서 서버로 보낼 body 조각을 구성 (undefined 필드는 제외) */
const buildAuthBody = (opts?: SmsRequestOptions): Record<string, string> => {
  const body: Record<string, string> = {};
  if (opts?.purpose) body.purpose = opts.purpose;
  if (opts?.smsChallenge) body.smsChallenge = opts.smsChallenge;
  return body;
};

/** 서버 error.code 를 보존하는 예외 (challenge 만료/재사용 감지용 [C-20]) */
export interface SmsError extends Error {
  code?: string;
}

/** 실패 응답을 code 를 보존한 Error 로 변환해 throw */
const throwSmsError = (data: { error?: { code?: string; message?: string } }, fallback: string): never => {
  const err = new Error(data?.error?.message || fallback) as SmsError;
  err.code = data?.error?.code;
  throw err;
};

/**
 * SMS 인증번호 발송
 * @param phoneNumber - 전화번호 (예: 01012345678)
 * @param opts - purpose / smsChallenge (user_id 미전송)
 */
export const sendVerificationCode = async (
  phoneNumber: string,
  opts?: SmsRequestOptions
): Promise<SendSMSResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/auth/sms/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone_number: phoneNumber, ...buildAuthBody(opts) }),
  });

  const data = await response.json();
  if (!response.ok) {
    return throwSmsError(data, '인증번호 발송에 실패했습니다.');
  }
  return data;
};

/**
 * SMS 인증번호 확인
 * @param phoneNumber - 전화번호
 * @param code - 인증번호 (6자리)
 * @param opts - purpose / smsChallenge
 */
export const verifyCode = async (
  phoneNumber: string,
  code: string,
  opts?: SmsRequestOptions
): Promise<VerifySMSResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/auth/sms/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone_number: phoneNumber, code, ...buildAuthBody(opts) }),
  });

  const data = await response.json();
  if (!response.ok) {
    return throwSmsError(data, '인증번호 확인에 실패했습니다.');
  }
  return data;
};

/**
 * SMS 인증번호 재발송 — send 와 동일 계약(purpose/challenge) [C-33]
 * @param phoneNumber - 전화번호
 * @param opts - purpose / smsChallenge
 */
export const resendCode = async (
  phoneNumber: string,
  opts?: SmsRequestOptions
): Promise<SendSMSResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/auth/sms/resend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone_number: phoneNumber, ...buildAuthBody(opts) }),
  });

  const data = await response.json();
  if (!response.ok) {
    return throwSmsError(data, '인증번호 재발송에 실패했습니다.');
  }
  return data;
};

/**
 * SMS 서비스 상태 조회
 */
export const getSMSStatus = async (): Promise<SMSStatusResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/auth/sms/status`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error('SMS 서비스 상태 조회에 실패했습니다.');
  }
  return data;
};

export default {
  sendVerificationCode,
  verifyCode,
  resendCode,
  getSMSStatus,
};
